import { BaseController } from '../../../../base_controller';
import zoomSdk, { ConfigResponse } from '@zoom/appssdk';
import { configureZoomSdk, subscribeToZoomMessages, supportsZoomApi } from '../zoom_sdk';

type InternalMessage = {
  id: string;
  data?: object;
};

export const MESSAGES = {
  FOCUS_APP: 'focus_app',
  UNFOCUS_APP: 'unfocus_app',
};

export default class extends BaseController {
  static targets = ['errorMessage'];

  declare errorMessageTarget: HTMLElement;

  static values = {
    userHost: Boolean,
    inMeeting: Boolean,
  };

  declare userHostValue: boolean;
  declare inMeetingValue: boolean;
  private config?: ConfigResponse;
  private unsubscribeMessages?: () => void;

  async connect(): Promise<void> {
    if (this.userHostValue) return;

    try {
      const config = await configureZoomSdk();
      if (!this.element.isConnected) return;

      this.config = config;
      this.logger('Zoom JS SDK Configuration', config);

      if (!supportsZoomApi(config, 'onMessage')) {
        this.showErrorMessage('This Zoom client cannot receive app messages.');
        return;
      }

      this.setupListeners();
    } catch (error) {
      console.error(error);
      this.showErrorMessage('Could not connect to Zoom. Reopen the app and try again.');
    }
  }

  disconnect(): void {
    this.unsubscribeMessages?.();
    this.unsubscribeMessages = undefined;
  }

  // private methods

  private async focusApp(): Promise<void> {
    const frontResponse = await zoomSdk.bringAppToFront();
    if ('warning' in frontResponse || frontResponse.message !== 'Success') {
      throw new Error('Zoom did not bring the app to the front');
    }

    const expandResponse = await zoomSdk.expandApp({ action: 'expand' });
    if (expandResponse.message !== 'Success') throw new Error('Zoom did not expand the app');
  }

  private async unfocusApp(): Promise<void> {
    const response = await zoomSdk.sendAppToBackground();
    if ('warning' in response || response.message !== 'Success') {
      throw new Error('Zoom did not send the app to the background');
    }
  }

  private async handleMessage(message: InternalMessage): Promise<void> {
    if (this.userHostValue || !message || !this.config) return;

    try {
      this.hideErrorMessage();

      if (message.id === MESSAGES.FOCUS_APP) {
        if (!supportsZoomApi(this.config, 'bringAppToFront') ||
          !supportsZoomApi(this.config, 'expandApp')) {
          this.showErrorMessage('This Zoom client cannot show the app automatically.');
          return;
        }

        await this.focusApp();
      } else if (message.id === MESSAGES.UNFOCUS_APP) {
        if (!supportsZoomApi(this.config, 'sendAppToBackground')) {
          this.showErrorMessage('This Zoom client cannot hide the app automatically.');
          return;
        }

        await this.unfocusApp();
      }
    } catch (error) {
      console.error(error);
      this.showErrorMessage('Zoom could not change the app window.');
    }
  }

  private setupListeners(): void {
    if (this.unsubscribeMessages) return;

    this.unsubscribeMessages = subscribeToZoomMessages(({ timestamp, payload }) => {
      this.logger(`(${timestamp}) New message received:`, payload);

      this.handleMessage(payload as InternalMessage);
    });
  }

  protected logger(message, details): void {
    const finalMessage = `[ZoomIntegration] ${message}`;

    super.logger(finalMessage, details, '#b526b0');
  }

  private showErrorMessage(message: string): void {
    this.errorMessageTarget.classList.remove('hide');
    this.errorMessageTarget.textContent = message;
  }

  private hideErrorMessage(): void {
    this.errorMessageTarget.classList.add('hide');
  }
}
